// Partes menores do painel do detalhe: dica "?", camadas por cima do detalhe (openOver, folhas de menu), ligações
// (cita / citado em), texto e objetos reconhecidos, privado.
import type { ButtonHTMLAttributes, ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { Card } from "../../../../shared/types.ts";
import { cardText } from "../lib.ts";
import { byId, closeCard, failed, modal, searchFor, showCard, showModal, store, width } from "../state.ts";
import { IconLock, IconText } from "./icons.tsx";
import { reachable } from "./links.ts";

/** Toque (celular, tablet): sem hover, alvos de 44 px, menus em folha. */
export const coarse = () => matchMedia("(pointer: coarse)").matches;

/** Bolinha "?" com dica: hover e foco no computador; tocar mostra o texto embaixo (no toque não há hover). */
export function Hint({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" class="hint" aria-label={`Ajuda: ${text}`} aria-expanded={open} data-tip={text} onClick={() => setOpen(!open)}>
        ?
      </button>
      {open && (
        <p class="hint-text" aria-hidden="true">
          {text}
        </p>
      )}
    </>
  );
}

/**
 * Diálogo por cima do detalhe (Parecidos, leitura, escrita, imagem, folhas de menu). Ao fechar, os avisos voltam para o
 * diálogo de baixo. Cada camada é uma entrada no histórico: voltar (gesto do iOS, botão do Android) fecha só a de cima,
 * e fechar pelo × ou Esc tira a entrada. No toque (`self`) o foco vai para o próprio diálogo, não para o × (anel a cada
 * abertura); o leitor de tela anuncia o nome do diálogo.
 * Chamar `close()` no cleanup do efeito: tirar um <dialog> aberto do DOM não dispara "close".
 */
export function openOver(d: HTMLDialogElement, self = coarse()) {
  const under = modal.peek();
  showModal(d);
  if (self) {
    d.tabIndex = -1;
    d.focus();
  }
  const depth = (history.state?.layer ?? 0) + 1;
  history.pushState({ ...history.state, layer: depth }, "", location.href);
  const onPop = () => (history.state?.layer ?? 0) < depth && d.close();
  addEventListener("popstate", onPop);
  d.addEventListener(
    "close",
    () => {
      removeEventListener("popstate", onPop);
      if (history.state?.layer === depth) history.back();
      if (under?.open) modal.value = under;
    },
    { once: true },
  );
  return () => d.open && d.close();
}

/**
 * iOS só abre o teclado para um foco dado dentro do toque. Quem abre um editor depois (modo escrita) segura o teclado
 * num campo invisível agora e o editor o herda (o teclado fica quando o foco passa de um campo a outro).
 */
export function holdKeyboard() {
  const host = modal.peek();
  if (!coarse() || !host) return;
  const t = document.createElement("input");
  t.className = "rd-focus-proxy";
  t.setAttribute("aria-hidden", "true");
  t.tabIndex = -1;
  host.append(t);
  t.focus();
}
export const releaseKeyboard = () => document.querySelectorAll(".rd-focus-proxy").forEach((t) => t.remove());

type Trigger = ButtonHTMLAttributes<HTMLButtonElement>;
/**
 * Menu do detalhe (tipo, ação, capa): popover ancorado no botão no computador; no toque (celular deitado também) e até
 * 768, folha modal presa embaixo. Modal porque tocar fora de um popover também tocava no que estava atrás (uma etiqueta
 * fechava o detalhe, "Pôr em foco" fixava o card); aqui o fundo fica inerte e o toque fora só fecha a folha.
 */
export function MenuSheet({ id, label, trigger, children, class: cls = "" }: { id: string; label: string; trigger: (p: Trigger) => JSX.Element; children: ComponentChildren; class?: string }) {
  const [open, setOpen] = useState(false);
  if (width.value > 768 && !coarse()) {
    return (
      <>
        {trigger({ popovertarget: id } as Trigger)}
        <div id={id} popover="auto" class={`pop menu rd-menu ${cls}`} role="group" aria-label={label}>
          {children}
        </div>
      </>
    );
  }
  return (
    <>
      {trigger({ "aria-haspopup": "dialog", onClick: () => setOpen(true) })}
      {open && (
        <SheetMenu id={id} label={label} cls={cls} onClose={() => setOpen(false)}>
          {children}
        </SheetMenu>
      )}
    </>
  );
}

function SheetMenu({ id, label, cls, onClose, children }: { id: string; label: string; cls: string; onClose: () => void; children: ComponentChildren }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => openOver(ref.current!), []);
  return (
    <dialog ref={ref} id={id} class="rd-sheet-menu" aria-label={label} onClose={onClose} onClick={(e) => e.target === e.currentTarget && e.currentTarget.close()}>
      <div class={`pop menu rd-menu ${cls}`} role="group" aria-label={label}>
        <span class="rd-grabber" aria-hidden="true" />
        <p class="rd-sheet-menu-title" aria-hidden="true">
          {label}
        </p>
        {children}
      </div>
    </dialog>
  );
}

/** Fecha o menu (popover ou folha) depois de uma escolha. */
export function closeMenu(id: string) {
  const el = document.getElementById(id);
  if (el instanceof HTMLDialogElement) el.close();
  else el?.hidePopover();
}

function Row({ c }: { c: Card }) {
  return (
    <li>
      <button type="button" class="rd-linkrow" onClick={() => showCard(c.id)}>
        {cardText(c).slice(0, 80)}
      </button>
    </li>
  );
}

/** [[ ]] que saem deste card e que chegam nele. */
export function Citations({ card: c }: { card: Card }) {
  const map = byId.value;
  const live = (x: Card | undefined): x is Card => !!x && !x.deletedAt && reachable(x, c.id);
  const out = store.outgoing(c.id).map((l) => map.get(l.targetId)).filter(live);
  const back = store.backlinks(c.id).map((l) => map.get(l.sourceId)).filter(live);
  if (!out.length && !back.length) return null;
  return (
    <section class="rd-links" aria-labelledby="links-label">
      <div class="section-head">
        <h3 class="section-label" id="links-label">
          Ligações
        </h3>
        <Hint text="Escreva [[ numa nota para ligar a outra." />
      </div>
      {back.length > 0 && (
        <>
          <p class="rd-sub">Citado em</p>
          <ul class="rd-linklist">{back.map((x) => <Row key={x.id} c={x} />)}</ul>
        </>
      )}
      {out.length > 0 && (
        <>
          <p class="rd-sub">Cita</p>
          <ul class="rd-linklist">{out.map((x) => <Row key={x.id} c={x} />)}</ul>
        </>
      )}
    </section>
  );
}

const langName = (code: string) => {
  try {
    return new Intl.DisplayNames(["pt-BR"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
};
const token = (o: string) => (/\s/.test(o) ? `objeto:"${o}"` : `objeto:${o}`);

/** OCR, objetos e idioma (busca objeto: e idioma:). Fechado por padrão. */
export function Recognized({ card: c }: { card: Card }) {
  if (!c.ocrText && !c.objects.length) return null;
  return (
    <details class="rd-ocr">
      <summary>
        <IconText />
        Texto e objetos reconhecidos
      </summary>
      {c.objects.length > 0 && (
        <>
          <p class="rd-sub">Objetos</p>
          <ul class="tag-list">
            {c.objects.map((o) => (
              <li class="tag" key={o}>
                <button type="button" class="tag-name" title={`Buscar ${token(o)}`} onClick={() => closeCard(() => searchFor(token(o)))}>
                  {o}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {c.ocrText && (
        <>
          <p class="rd-sub">Texto na imagem</p>
          <p class="rd-ocr-text">{c.ocrText}</p>
        </>
      )}
      {c.language && <p class="rd-sub rd-lang">Idioma: {langName(c.language)}</p>}
    </details>
  );
}

/** Etiqueta reservada "privado" (§4.2): some da grade, da busca comum e do Reencontro. A UI diz que não é cifra. */
export function PrivateToggle({ card: c }: { card: Card }) {
  return (
    <div class="rd-private">
      <button type="button" role="switch" aria-checked={c.isPrivate} aria-describedby="private-why" class="rd-switch" onClick={() => store.setPrivate(c.id, !c.isPrivate).catch(failed)}>
        <IconLock />
        <span>Card privado</span>
        <span class="rd-track" aria-hidden="true" />
      </button>
      <p id="private-why" class="rd-why">
        Fica fora da grade, da busca e do Reencontro; aparece só buscando <code>#privado</code>. Não é criptografia.
      </p>
    </div>
  );
}
